import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Calculator,
  ArrowRight,
  TrendingUp,
  DollarSign,
  Percent,
  Calendar,
  Sparkles,
  PieChart,
  ShieldCheck,
  CreditCard,
  Home,
  PiggyBank,
  Coins,
  BarChart3,
  Receipt,
  RotateCcw,
} from "lucide-react";
import { AnimatedNumber } from "./Animations";
import { CATEGORY_ITEMS } from "./CategoryCards";

interface CalculatorCardProps {
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
}

export const CalculatorCard: React.FC<CalculatorCardProps> = ({
  selectedCategory,
  onSelectCategory,
}) => {
  // Map category to tool slug
  const categoryToSlug: Record<string, { slug: string; name: string }> = {
    mortgage: { slug: "mortgage-calculator", name: "Mortgage Calculator" },
    loan: { slug: "loan-calculator", name: "Loan Calculator" },
    investment: { slug: "compound-interest-calculator", name: "Compound Interest Calculator" },
    savings: { slug: "savings-calculator", name: "Savings Calculator" },
    retirement: { slug: "retirement-calculator", name: "Retirement Calculator" },
    tax: { slug: "tax-calculator", name: "Income Tax Calculator" },
    debt: { slug: "debt-payoff-calculator", name: "Debt Payoff Calculator" },
    budget: { slug: "budget-planner", name: "Budget Planner" },
  };

  const currentToolInfo = categoryToSlug[selectedCategory] || categoryToSlug["investment"];

  // --- STATE FOR ALL 8 CALCULATOR TYPES ---
  // 1. Investment (Compound Interest)
  const [invInitial, setInvInitial] = useState<number>(10000);
  const [invMonthly, setInvMonthly] = useState<number>(500);
  const [invRate, setInvRate] = useState<number>(8);
  const [invYears, setInvYears] = useState<number>(10);

  // 2. Mortgage
  const [mortPrice, setMortPrice] = useState<number>(400000);
  const [mortDown, setMortDown] = useState<number>(80000); // 20%
  const [mortRate, setMortRate] = useState<number>(6.5);
  const [mortYears, setMortYears] = useState<number>(30);

  // 3. Loan
  const [loanAmount, setLoanAmount] = useState<number>(25000);
  const [loanRate, setLoanRate] = useState<number>(7.5);
  const [loanYears, setLoanYears] = useState<number>(5);

  // 4. Savings
  const [savInitial, setSavInitial] = useState<number>(5000);
  const [savMonthly, setSavMonthly] = useState<number>(300);
  const [savApy, setSavApy] = useState<number>(4.5);
  const [savYears, setSavYears] = useState<number>(5);

  // 5. Retirement
  const [retCurrentAge, setRetCurrentAge] = useState<number>(30);
  const [retTargetAge, setRetTargetAge] = useState<number>(65);
  const [retCurrentSavings, setRetCurrentSavings] = useState<number>(25000);
  const [retMonthlySavings, setRetMonthlySavings] = useState<number>(800);

  // 6. Tax
  const [taxIncome, setTaxIncome] = useState<number>(85000);
  const [taxFilingStatus, setTaxFilingStatus] = useState<string>("single"); // single | joint
  const [taxStateRate, setTaxStateRate] = useState<number>(5);

  // 7. Debt
  const [debtAmount, setDebtAmount] = useState<number>(15000);
  const [debtRate, setDebtRate] = useState<number>(18.9);
  const [debtMonthly, setDebtMonthly] = useState<number>(450);

  // 8. Budget
  const [budIncome, setBudIncome] = useState<number>(5500);

  // --- CALCULATOR LOGIC COMPUTATIONS ---

  // 1. Investment Calculation
  const invResults = useMemo(() => {
    const P = invInitial;
    const PMT = invMonthly;
    const r = invRate / 100 / 12;
    const n = invYears * 12;

    let futureValue = P * Math.pow(1 + r, n);
    if (r > 0) {
      futureValue += PMT * ((Math.pow(1 + r, n) - 1) / r);
    } else {
      futureValue += PMT * n;
    }

    const totalContributions = P + PMT * n;
    const interestEarned = Math.max(0, futureValue - totalContributions);

    return {
      futureValue: Math.round(futureValue),
      totalContributions: Math.round(totalContributions),
      interestEarned: Math.round(interestEarned),
    };
  }, [invInitial, invMonthly, invRate, invYears]);

  // 2. Mortgage Calculation
  const mortResults = useMemo(() => {
    const loanAmt = Math.max(0, mortPrice - mortDown);
    const r = mortRate / 100 / 12;
    const n = mortYears * 12;

    let monthlyPayment = 0;
    if (r > 0) {
      monthlyPayment = (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      monthlyPayment = loanAmt / n;
    }

    const totalCost = monthlyPayment * n;
    const totalInterest = Math.max(0, totalCost - loanAmt);

    return {
      loanAmt: Math.round(loanAmt),
      monthlyPayment: Math.round(monthlyPayment),
      totalInterest: Math.round(totalInterest),
      totalCost: Math.round(totalCost),
    };
  }, [mortPrice, mortDown, mortRate, mortYears]);

  // 3. Loan Calculation
  const loanResults = useMemo(() => {
    const r = loanRate / 100 / 12;
    const n = loanYears * 12;

    let monthlyPayment = 0;
    if (r > 0) {
      monthlyPayment = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      monthlyPayment = loanAmount / n;
    }

    const totalPaid = monthlyPayment * n;
    const totalInterest = Math.max(0, totalPaid - loanAmount);

    return {
      monthlyPayment: Math.round(monthlyPayment),
      totalInterest: Math.round(totalInterest),
      totalPaid: Math.round(totalPaid),
    };
  }, [loanAmount, loanRate, loanYears]);

  // 4. Savings Calculation
  const savResults = useMemo(() => {
    const P = savInitial;
    const PMT = savMonthly;
    const r = savApy / 100 / 12;
    const n = savYears * 12;

    let totalSavings = P * Math.pow(1 + r, n);
    if (r > 0) {
      totalSavings += PMT * ((Math.pow(1 + r, n) - 1) / r);
    } else {
      totalSavings += PMT * n;
    }

    const totalDeposited = P + PMT * n;
    const totalInterest = Math.max(0, totalSavings - totalDeposited);

    return {
      totalSavings: Math.round(totalSavings),
      totalDeposited: Math.round(totalDeposited),
      totalInterest: Math.round(totalInterest),
    };
  }, [savInitial, savMonthly, savApy, savYears]);

  // 5. Retirement Calculation
  const retResults = useMemo(() => {
    const years = Math.max(1, retTargetAge - retCurrentAge);
    const P = retCurrentSavings;
    const PMT = retMonthlySavings;
    const r = 0.07 / 12; // 7% assumed market return
    const n = years * 12;

    let nestEgg = P * Math.pow(1 + r, n);
    nestEgg += PMT * ((Math.pow(1 + r, n) - 1) / r);

    const totalContributions = P + PMT * n;
    // 4% safe withdrawal rate for monthly income estimate
    const monthlyRetirementIncome = (nestEgg * 0.04) / 12;

    return {
      yearsToRetire: years,
      nestEgg: Math.round(nestEgg),
      monthlyRetirementIncome: Math.round(monthlyRetirementIncome),
      totalContributions: Math.round(totalContributions),
    };
  }, [retCurrentAge, retTargetAge, retCurrentSavings, retMonthlySavings]);

  // 6. Tax Calculation
  const taxResults = useMemo(() => {
    const standardDeduction = taxFilingStatus === "joint" ? 29200 : 14600;
    const taxableIncome = Math.max(0, taxIncome - standardDeduction);

    // Progressive Federal Brackets Simplified
    let fedTax = 0;
    if (taxableIncome > 0) {
      if (taxableIncome <= 11600) fedTax = taxableIncome * 0.1;
      else if (taxableIncome <= 47150) fedTax = 1160 + (taxableIncome - 11600) * 0.12;
      else if (taxableIncome <= 100525) fedTax = 5426 + (taxableIncome - 47150) * 0.22;
      else fedTax = 17148.5 + (taxableIncome - 100525) * 0.24;
    }

    const stateTax = taxIncome * (taxStateRate / 100);
    const totalTax = fedTax + stateTax;
    const takeHomePay = Math.max(0, taxIncome - totalTax);
    const effectiveRate = taxIncome > 0 ? (totalTax / taxIncome) * 100 : 0;

    return {
      fedTax: Math.round(fedTax),
      effectiveRate: Number(effectiveRate.toFixed(1)),
      takeHomePay: Math.round(takeHomePay),
      monthlyTakeHome: Math.round(takeHomePay / 12),
    };
  }, [taxIncome, taxFilingStatus, taxStateRate]);

  // 7. Debt Payoff Calculation
  const debtResults = useMemo(() => {
    const P = debtAmount;
    const r = debtRate / 100 / 12;
    const PMT = debtMonthly;

    if (PMT <= P * r) {
      // Payment too low to cover monthly interest
      return { months: 999, totalInterest: 999999, totalPaid: 999999, isImpossible: true };
    }

    const n = -Math.log(1 - (r * P) / PMT) / Math.log(1 + r);
    const months = Math.ceil(n);
    const totalPaid = PMT * months;
    const totalInterest = Math.max(0, totalPaid - P);

    return {
      months,
      years: (months / 12).toFixed(1),
      totalInterest: Math.round(totalInterest),
      totalPaid: Math.round(totalPaid),
      isImpossible: false,
    };
  }, [debtAmount, debtRate, debtMonthly]);

  // 8. Budget Calculation
  const budResults = useMemo(() => {
    const needs = budIncome * 0.5;
    const wants = budIncome * 0.3;
    const savings = budIncome * 0.2;

    return {
      needs: Math.round(needs),
      wants: Math.round(wants),
      savings: Math.round(savings),
    };
  }, [budIncome]);

  return (
    <div className="relative w-full bg-white/[0.05] backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-7 shadow-2xl overflow-hidden text-left group transition-all duration-300 hover:border-amber-500/40">
      {/* Background ambient gold aura */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none group-hover:bg-amber-500/20 transition-all duration-500" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Calculator Header & Selector Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="p-2.5 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-400">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-sans font-bold text-lg sm:text-xl text-white tracking-tight">
                {currentToolInfo.name}
              </h3>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
              </span>
            </div>
            <p className="text-xs text-slate-400 font-light">
              Live Interactive Calculator Model
            </p>
          </div>
        </div>

        {/* Quick Category Selector Dropdown/Pills */}
        <div className="relative">
          <select
            value={selectedCategory}
            onChange={(e) => onSelectCategory(e.target.value)}
            className="bg-[#0b1426] text-amber-300 font-semibold text-xs border border-amber-500/40 rounded-xl px-3 py-2 outline-none cursor-pointer hover:border-amber-400 transition-colors"
          >
            {CATEGORY_ITEMS.map((item) => (
              <option key={item.id} value={item.id} className="bg-[#081122] text-white">
                {item.name} Calculator
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Dynamic Animated Calculator View */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="space-y-6"
        >
          {/* ================= 1. INVESTMENT CALCULATOR ================= */}
          {selectedCategory === "investment" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Initial Amount</span>
                    <span className="text-amber-400 font-mono font-semibold">${invInitial.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="1000"
                    value={invInitial}
                    onChange={(e) => setInvInitial(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Monthly Contribution</span>
                    <span className="text-amber-400 font-mono font-semibold">${invMonthly.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="50"
                    value={invMonthly}
                    onChange={(e) => setInvMonthly(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Expected Return (APY)</span>
                    <span className="text-amber-400 font-mono font-semibold">{invRate}%</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="18"
                    step="0.5"
                    value={invRate}
                    onChange={(e) => setInvRate(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Investment Period</span>
                    <span className="text-amber-400 font-mono font-semibold">{invYears} Years</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="40"
                    step="1"
                    value={invYears}
                    onChange={(e) => setInvYears(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results Cards */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    PROJECTED FUTURE VALUE
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={invResults.futureValue} prefix="$" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Total Contributed</span>
                    <span className="text-white font-mono font-bold text-sm">
                      <AnimatedNumber value={invResults.totalContributions} prefix="$" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Interest Earned</span>
                    <span className="text-emerald-400 font-mono font-bold text-sm">
                      <AnimatedNumber value={invResults.interestEarned} prefix="+$" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 2. MORTGAGE CALCULATOR ================= */}
          {selectedCategory === "mortgage" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Home Price</span>
                    <span className="text-amber-400 font-mono font-semibold">${mortPrice.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="100000"
                    max="1500000"
                    step="10000"
                    value={mortPrice}
                    onChange={(e) => setMortPrice(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Down Payment</span>
                    <span className="text-amber-400 font-mono font-semibold">${mortDown.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={mortPrice * 0.5}
                    step="5000"
                    value={mortDown}
                    onChange={(e) => setMortDown(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Mortgage Rate</span>
                    <span className="text-amber-400 font-mono font-semibold">{mortRate}%</span>
                  </label>
                  <input
                    type="range"
                    min="3"
                    max="12"
                    step="0.25"
                    value={mortRate}
                    onChange={(e) => setMortRate(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Loan Term</span>
                    <span className="text-amber-400 font-mono font-semibold">{mortYears} Years</span>
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="30"
                    step="5"
                    value={mortYears}
                    onChange={(e) => setMortYears(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    ESTIMATED MONTHLY PAYMENT (P&amp;I)
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={mortResults.monthlyPayment} prefix="$" suffix="/mo" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Loan Amount</span>
                    <span className="text-white font-mono font-bold text-sm">
                      <AnimatedNumber value={mortResults.loanAmt} prefix="$" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Total Interest Paid</span>
                    <span className="text-amber-300 font-mono font-bold text-sm">
                      <AnimatedNumber value={mortResults.totalInterest} prefix="$" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 3. LOAN CALCULATOR ================= */}
          {selectedCategory === "loan" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Loan Amount</span>
                    <span className="text-amber-400 font-mono font-semibold">${loanAmount.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Interest Rate</span>
                    <span className="text-amber-400 font-mono font-semibold">{loanRate}%</span>
                  </label>
                  <input
                    type="range"
                    min="2"
                    max="25"
                    step="0.5"
                    value={loanRate}
                    onChange={(e) => setLoanRate(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Term Length</span>
                    <span className="text-amber-400 font-mono font-semibold">{loanYears} Years</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={loanYears}
                    onChange={(e) => setLoanYears(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    ESTIMATED MONTHLY PAYMENT
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={loanResults.monthlyPayment} prefix="$" suffix="/mo" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Total Interest</span>
                    <span className="text-amber-300 font-mono font-bold text-sm">
                      <AnimatedNumber value={loanResults.totalInterest} prefix="$" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Total Repaid</span>
                    <span className="text-white font-mono font-bold text-sm">
                      <AnimatedNumber value={loanResults.totalPaid} prefix="$" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 4. SAVINGS CALCULATOR ================= */}
          {selectedCategory === "savings" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Starting Balance</span>
                    <span className="text-amber-400 font-mono font-semibold">${savInitial.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50000"
                    step="500"
                    value={savInitial}
                    onChange={(e) => setSavInitial(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Monthly Savings</span>
                    <span className="text-amber-400 font-mono font-semibold">${savMonthly.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="3000"
                    step="50"
                    value={savMonthly}
                    onChange={(e) => setSavMonthly(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Savings APY Rate</span>
                    <span className="text-amber-400 font-mono font-semibold">{savApy}%</span>
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="10"
                    step="0.25"
                    value={savApy}
                    onChange={(e) => setSavApy(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Time Horizon</span>
                    <span className="text-amber-400 font-mono font-semibold">{savYears} Years</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={savYears}
                    onChange={(e) => setSavYears(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    PROJECTED TOTAL SAVINGS
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={savResults.totalSavings} prefix="$" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Total Deposits</span>
                    <span className="text-white font-mono font-bold text-sm">
                      <AnimatedNumber value={savResults.totalDeposited} prefix="$" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Interest Gained</span>
                    <span className="text-emerald-400 font-mono font-bold text-sm">
                      <AnimatedNumber value={savResults.totalInterest} prefix="+$" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 5. RETIREMENT CALCULATOR ================= */}
          {selectedCategory === "retirement" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Current Age</span>
                    <span className="text-amber-400 font-mono font-semibold">{retCurrentAge} Yrs</span>
                  </label>
                  <input
                    type="range"
                    min="18"
                    max="70"
                    step="1"
                    value={retCurrentAge}
                    onChange={(e) => setRetCurrentAge(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Retirement Target Age</span>
                    <span className="text-amber-400 font-mono font-semibold">{retTargetAge} Yrs</span>
                  </label>
                  <input
                    type="range"
                    min="45"
                    max="75"
                    step="1"
                    value={retTargetAge}
                    onChange={(e) => setRetTargetAge(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Current Savings</span>
                    <span className="text-amber-400 font-mono font-semibold">${retCurrentSavings.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="200000"
                    step="5000"
                    value={retCurrentSavings}
                    onChange={(e) => setRetCurrentSavings(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Monthly Savings</span>
                    <span className="text-amber-400 font-mono font-semibold">${retMonthlySavings.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="100"
                    value={retMonthlySavings}
                    onChange={(e) => setRetMonthlySavings(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    PROJECTED RETIREMENT NEST EGG
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={retResults.nestEgg} prefix="$" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Est. Monthly Income (4%)</span>
                    <span className="text-emerald-400 font-mono font-bold text-sm">
                      <AnimatedNumber value={retResults.monthlyRetirementIncome} prefix="$" suffix="/mo" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Timeline Remaining</span>
                    <span className="text-white font-mono font-bold text-sm">
                      {retResults.yearsToRetire} Years
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 6. TAX CALCULATOR ================= */}
          {selectedCategory === "tax" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Gross Annual Salary</span>
                    <span className="text-amber-400 font-mono font-semibold">${taxIncome.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="20000"
                    max="300000"
                    step="5000"
                    value={taxIncome}
                    onChange={(e) => setTaxIncome(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300">Filing Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setTaxFilingStatus("single")}
                      className={`py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                        taxFilingStatus === "single"
                          ? "bg-amber-500 text-slate-950 border-amber-400 font-bold"
                          : "bg-white/5 text-slate-300 border-white/10"
                      }`}
                    >
                      Single
                    </button>
                    <button
                      onClick={() => setTaxFilingStatus("joint")}
                      className={`py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                        taxFilingStatus === "joint"
                          ? "bg-amber-500 text-slate-950 border-amber-400 font-bold"
                          : "bg-white/5 text-slate-300 border-white/10"
                      }`}
                    >
                      Married Joint
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>State Tax Est.</span>
                    <span className="text-amber-400 font-mono font-semibold">{taxStateRate}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="12"
                    step="0.5"
                    value={taxStateRate}
                    onChange={(e) => setTaxStateRate(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    ESTIMATED NET TAKE-HOME PAY
                  </span>
                  <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                    <AnimatedNumber value={taxResults.takeHomePay} prefix="$" suffix="/yr" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                  <div>
                    <span className="text-slate-400 font-light block">Monthly Take-Home</span>
                    <span className="text-emerald-400 font-mono font-bold text-sm">
                      <AnimatedNumber value={taxResults.monthlyTakeHome} prefix="$" suffix="/mo" />
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-light block">Effective Tax Rate</span>
                    <span className="text-white font-mono font-bold text-sm">
                      {taxResults.effectiveRate}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================= 7. DEBT CALCULATOR ================= */}
          {selectedCategory === "debt" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Total Debt Balance</span>
                    <span className="text-amber-400 font-mono font-semibold">${debtAmount.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={debtAmount}
                    onChange={(e) => setDebtAmount(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Average APR Rate</span>
                    <span className="text-amber-400 font-mono font-semibold">{debtRate}%</span>
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="32"
                    step="0.5"
                    value={debtRate}
                    onChange={(e) => setDebtRate(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs font-medium text-slate-300 flex justify-between">
                    <span>Target Monthly Payment</span>
                    <span className="text-amber-400 font-mono font-semibold">${debtMonthly.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min="100"
                    max="3000"
                    step="50"
                    value={debtMonthly}
                    onChange={(e) => setDebtMonthly(Number(e.target.value))}
                    className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                {debtResults.isImpossible ? (
                  <div className="text-center py-2 text-rose-400 text-xs font-semibold">
                    ⚠️ Payment is too low to cover monthly interest. Increase monthly payment!
                  </div>
                ) : (
                  <>
                    <div className="text-center sm:text-left space-y-1">
                      <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                        ESTIMATED TIME TO DEBT-FREE
                      </span>
                      <div className="text-3xl sm:text-4xl font-display font-extrabold text-amber-400 tracking-tight">
                        {debtResults.months} Months ({debtResults.years} Yrs)
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10 text-xs">
                      <div>
                        <span className="text-slate-400 font-light block">Total Interest Paid</span>
                        <span className="text-amber-300 font-mono font-bold text-sm">
                          <AnimatedNumber value={debtResults.totalInterest} prefix="$" />
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-light block">Total Out of Pocket</span>
                        <span className="text-white font-mono font-bold text-sm">
                          <AnimatedNumber value={debtResults.totalPaid} prefix="$" />
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ================= 8. BUDGET CALCULATOR ================= */}
          {selectedCategory === "budget" && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300 flex justify-between">
                  <span>Monthly Net Income</span>
                  <span className="text-amber-400 font-mono font-semibold">${budIncome.toLocaleString()}</span>
                </label>
                <input
                  type="range"
                  min="1000"
                  max="20000"
                  step="250"
                  value={budIncome}
                  onChange={(e) => setBudIncome(Number(e.target.value))}
                  className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-amber-500"
                />
              </div>

              {/* Results */}
              <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 sm:p-5 space-y-4">
                <div className="text-center sm:text-left space-y-1">
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 uppercase block">
                    50/30/20 BUDGET BREAKDOWN
                  </span>
                  <div className="text-2xl sm:text-3xl font-display font-extrabold text-white tracking-tight">
                    ${budIncome.toLocaleString()} <span className="text-xs font-light text-slate-400">/ month</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/10 text-xs text-center">
                  <div className="bg-white/5 p-2 rounded-xl">
                    <span className="text-slate-400 text-[10px] block">Needs (50%)</span>
                    <span className="text-amber-400 font-mono font-bold text-xs sm:text-sm">
                      <AnimatedNumber value={budResults.needs} prefix="$" />
                    </span>
                  </div>
                  <div className="bg-white/5 p-2 rounded-xl">
                    <span className="text-slate-400 text-[10px] block">Wants (30%)</span>
                    <span className="text-blue-400 font-mono font-bold text-xs sm:text-sm">
                      <AnimatedNumber value={budResults.wants} prefix="$" />
                    </span>
                  </div>
                  <div className="bg-white/5 p-2 rounded-xl">
                    <span className="text-slate-400 text-[10px] block">Savings (20%)</span>
                    <span className="text-emerald-400 font-mono font-bold text-xs sm:text-sm">
                      <AnimatedNumber value={budResults.savings} prefix="$" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Direct CTA Link Button to Full Dedicated Calculator Route */}
          <div className="pt-2">
            <Link
              to={`/${currentToolInfo.slug}`}
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold rounded-xl text-sm transition-all duration-300 shadow-md shadow-amber-500/10 hover:shadow-amber-500/25 flex items-center justify-center gap-2 cursor-pointer group"
            >
              <span>Open Full {currentToolInfo.name}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
